/**
 * useAuthorization hook for checking user permissions client-side.
 *
 * This hook provides utilities to check if the current user has specific
 * permissions based on their role from the database (single source of truth).
 * It provides a consistent API for permission checks throughout the app.
 *
 * Feature: 001-user-role-management (User Story 1 - T038)
 * Security: Role is fetched from backend database, not from Clerk metadata
 */
import { useAuth } from '../contexts/AuthContext';

/**
 * Permission format: resource:action:scope
 * Examples:
 * - conversations:read:own
 * - conversations:read:all
 * - users:write:all
 * - admin:read:all
 */
export type Permission = string;

export type UserRole = 'admin' | 'user';

export interface AuthorizationContext {
  /**
   * Current user's role from Clerk publicMetadata
   */
  role: UserRole | null;

  /**
   * Check if user has a specific role
   */
  hasRole: (role: UserRole) => boolean;

  /**
   * Check if user has a specific permission
   * Format: resource:action:scope
   */
  hasPermission: (permission: Permission) => boolean;

  /**
   * Check if user can perform an action on a resource
   */
  canAccess: (resource: string, action: string, scope?: 'own' | 'all') => boolean;

  /**
   * Check if user is an admin
   */
  isAdmin: boolean;

  /**
   * Check if user is a regular user
   */
  isUser: boolean;

  /**
   * Check if authorization data is loaded
   */
  isLoaded: boolean;
}

/**
 * Role-based permission mappings
 * These should match the backend permission definitions
 */
const ADMIN_PERMISSIONS: Permission[] = [
  'admin:read:all',
  'users:read:all',
  'users:write:all',
  'users:invite:all',
  'users:sync:all',
  'users:assign_role:all',
  'roles:read:all',
  'conversations:read:all',
  'conversations:write:all',
  'conversations:delete:all',
  'feedback:write:all',
];

const USER_PERMISSIONS: Permission[] = [
  'conversations:read:own',
  'conversations:write:own',
  'conversations:delete:own',
  'feedback:write:own',
];

/**
 * Hook to access authorization context and check permissions based on database role
 */
export function useAuthorization(): AuthorizationContext {
  const { user, role: authRole, isAuthenticated } = useAuth();

  // Get user role from database (via AuthContext)
  const role = (authRole as UserRole) || null;
  const isLoaded = isAuthenticated !== undefined;

  // Get permissions based on role
  const permissions: Permission[] = role === 'admin'
    ? ADMIN_PERMISSIONS
    : role === 'user'
    ? USER_PERMISSIONS
    : [];

  /**
   * Check if user has a specific role
   */
  const hasRole = (requiredRole: UserRole): boolean => {
    if (!role) return false;
    // Admins have access to everything
    if (role === 'admin') return true;
    return role === requiredRole;
  };

  /**
   * Check if user has a specific permission
   */
  const hasPermission = (permission: Permission): boolean => {
    if (!role) return false;
    // Admins have all permissions
    if (role === 'admin') return true;
    return permissions.includes(permission);
  };

  /**
   * Check if user can access a resource with specific action and scope
   */
  const canAccess = (
    resource: string,
    action: string,
    scope: 'own' | 'all' = 'own'
  ): boolean => {
    if (!role) return false;

    // Admins can access everything
    if (role === 'admin') return true;

    // Build permission string
    const permission = `${resource}:${action}:${scope}`;
    return permissions.includes(permission);
  };

  return {
    role,
    hasRole,
    hasPermission,
    canAccess,
    isAdmin: role === 'admin',
    isUser: role === 'user',
    isLoaded,
  };
}

/**
 * Convenience hook to check if user is an admin
 */
export function useIsAdmin(): boolean {
  const { isAdmin } = useAuthorization();
  return isAdmin;
}

/**
 * Convenience hook to check if user has a specific permission
 */
export function useHasPermission(permission: Permission): boolean {
  const { hasPermission } = useAuthorization();
  return hasPermission(permission);
}

/**
 * Convenience hook to check if user can access a resource
 */
export function useCanAccess(
  resource: string,
  action: string,
  scope?: 'own' | 'all'
): boolean {
  const { canAccess } = useAuthorization();
  return canAccess(resource, action, scope);
}
